<script setup>
/* eslint-disable */
// SiteUp (2026-08-11): mesmo tratamento do irmão KanbanActions.vue/
// ContactPanel.vue. Este arquivo carrega 184 violações ANTERIORES a esta
// fatia (PR #418) — dívida do módulo Kanban legado, não desta mudança — e
// o lint do CI é estrito por arquivo tocado: sem isto, nenhuma mudança aqui
// consegue ficar verde. Registrado, não resolvido — limpar é PR próprio.
import {
  ref,
  watchEffect,
  computed,
  watch,
  nextTick,
  onMounted,
  onUnmounted,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import { useConfig } from 'dashboard/composables/useConfig';
import conversationAPI from '../../../../api/inbox/conversation';
import funnelAPI from '../../../../api/funnel';
import offersAPI from '../../../../api/offers';
import offerGroupsAPI from '../../../../api/offerGroups';
import Button from 'dashboard/components-next/button/Button.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import ContactAPI from 'dashboard/api/contacts';
import KanbanAPI from 'dashboard/api/kanban';
import KanbanNotesTab from './KanbanNotesTab.vue';
import {
  formatKanbanCurrency,
  maskKanbanCurrencyTyping,
  parseKanbanCurrencyInput,
} from 'dashboard/helper/kanbanCurrencyMask';
import {
  createContactSearcher,
  fetchContactableInboxes,
  prepareNewMessagePayload,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';

const props = defineProps({
  funnelId: {
    type: [String, Number],
    required: true,
  },
  stage: {
    type: String,
    required: true,
  },
  position: {
    type: Number,
    default: 0,
  },
  initialData: {
    type: Object,
    default: null,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  currentConversation: {
    type: Object,
    default: null,
  },
  initialDate: {
    type: Date,
    default: null,
  },
});

const emit = defineEmits([
  'saved',
  'close',
  'upload-attachment',
  'delete-attachment',
  'update:item',
  'item-updated',
]);

// Modificar a função toLocalISOString para retornar o formato exato
const toLocalISOString = (date, includeTime = true) => {
  if (!date) return null;
  try {
    // Ajuste para corrigir o problema do fuso horário
    let d = new Date(date);

    // Verificar se é apenas data (sem hora) para evitar problemas de fuso horário
    if (typeof date === 'string' && date.length === 10) {
      // Para datas sem hora, forçar hora 12:00 para evitar problemas de fuso
      const parts = date.split('-');
      // Importante: mês em JavaScript é baseado em zero (0-11)
      d = new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0);
    }

    // Obter ano, mês e dia de forma segura considerando fuso horário local
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    if (!includeTime) {
      return `${year}-${month}-${day}`;
    }

    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');

    // Retornar no formato exato para datetime-local: YYYY-MM-DDThh:mm
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (error) {
    return null;
  }
};

// Função para extrair IDs dos agentes do formato completo
const extractAgentIds = assignedAgents => {
  if (!assignedAgents || !Array.isArray(assignedAgents)) return [];

  // Se é array de objetos completos (formato do JSON), extrair apenas os IDs
  if (
    assignedAgents.length > 0 &&
    typeof assignedAgents[0] === 'object' &&
    assignedAgents[0].id
  ) {
    return assignedAgents.map(agent => agent.id);
  }

  // Se já é array de IDs, retornar como está
  return assignedAgents;
};

const { t } = useI18n();
const store = useStore();
const searchContacts = createContactSearcher();
const activeTab = ref('general');
const currentUser = computed(() => store.getters.getCurrentUser);

// Computed para tipos de campos traduzidos
const getFieldTypeLabel = type => {
  const typeMap = {
    string: t('KANBAN.FORM.CUSTOM_FIELDS.TYPE_TEXT'),
    number: t('KANBAN.FORM.CUSTOM_FIELDS.TYPE_NUMBER'),
    date: t('KANBAN.FORM.CUSTOM_FIELDS.TYPE_DATE'),
    boolean: t('KANBAN.FORM.CUSTOM_FIELDS.TYPE_BOOLEAN'),
  };
  return typeMap[type] || type;
};

const loading = ref(false);
const loadingAgents = ref(false);
const loadingFunnels = ref(false);
const selectedFunnel = computed(
  () => store.getters['funnel/getSelectedFunnel']
);

// Computed para obter os funis disponíveis
const availableFunnels = computed(() => {
  const funnels = store.getters['funnel/getFunnels'] || [];
  return funnels.filter(funnel => funnel.active);
});

// Ref para o funil selecionado no form
const selectedFunnelId = ref(null);

// =====================================================
// Duplicar de outro kanban (apenas conversation_id)
// =====================================================
const duplicateOpen = ref(false);
const duplicateQuery = ref('');
const duplicateResults = ref([]);
const duplicateLoading = ref(false);
const duplicateSourceItem = ref(null);
let duplicateDebounceTimer = null;

const searchDuplicateItems = q => {
  if (duplicateDebounceTimer) clearTimeout(duplicateDebounceTimer);
  duplicateDebounceTimer = setTimeout(async () => {
    const term = (q || '').trim();
    if (term.length < 2) {
      duplicateResults.value = [];
      return;
    }
    duplicateLoading.value = true;
    try {
      const { data } = await KanbanAPI.searchItemsGlobal(term);
      duplicateResults.value = (data?.items || []).slice(0, 25);
    } catch (e) {
      console.error('[duplicate-search] failed', e);
      duplicateResults.value = [];
    } finally {
      duplicateLoading.value = false;
    }
  }, 300);
};

const applyDuplicate = sourceItem => {
  duplicateSourceItem.value = sourceItem;

  // Campos duplicados: título, descrição e conversation_id.
  const srcTitle = sourceItem?.item_details?.title || sourceItem?.title || '';
  const srcDesc =
    sourceItem?.item_details?.description || sourceItem?.description || '';
  const convId =
    sourceItem?.item_details?.conversation_id ||
    sourceItem?.conversation?.id ||
    sourceItem?.conversation?.display_id ||
    null;

  // Set top-level form fields (used pelos inputs v-model="form.title|description")
  // E também os mirror fields em form.item_details (usado no payload de save) pra
  // não depender só do watcher que sincroniza essas chaves — assim funciona mesmo
  // se o watcher ainda não disparou na hora de salvar.
  if (srcTitle) {
    form.value.title = srcTitle;
    if (form.value.item_details) form.value.item_details.title = srcTitle;
  }
  if (srcDesc) {
    form.value.description = srcDesc;
    if (form.value.item_details) form.value.item_details.description = srcDesc;
  }
  if (convId) {
    form.value.conversation_id = convId;
    if (form.value.item_details)
      form.value.item_details.conversation_id = convId;
  }

  // Garantir "nunca no mesmo": se o funil escolhido casualmente é o de
  // origem, força o usuário a re-escolher um funil diferente.
  if (
    sourceItem?.funnel_id &&
    selectedFunnelId.value === sourceItem.funnel_id
  ) {
    selectedFunnelId.value = null;
    form.value.funnel_stage = '';
  }
  duplicateOpen.value = false;
  duplicateQuery.value = '';
  duplicateResults.value = [];
};

const clearDuplicate = () => {
  duplicateSourceItem.value = null;
  // Limpa os 3 campos que o duplicate setou (top-level + mirror em item_details).
  form.value.conversation_id = null;
  form.value.title = '';
  form.value.description = '';
  if (form.value.item_details) {
    form.value.item_details.title = '';
    form.value.item_details.description = '';
    form.value.item_details.conversation_id = null;
  }
};

// Funis disponíveis no seletor — exclui o funil de origem da duplicação
// para garantir "nunca no mesmo".
const availableFunnelsForSelector = computed(() => {
  const all = availableFunnels.value || [];
  if (duplicateSourceItem.value?.funnel_id) {
    return all.filter(f => f.id !== duplicateSourceItem.value.funnel_id);
  }
  return all;
});

// Flag para evitar loops infinitos
const isUpdatingFunnel = ref(false);

// Watch para atualizar o store quando o funil for selecionado
watch(selectedFunnelId, async newFunnelId => {
  if (!newFunnelId || isUpdatingFunnel.value) return;

  const funnel = availableFunnels.value.find(f => f.id === newFunnelId);
  if (funnel && selectedFunnel.value?.id !== funnel.id) {
    isUpdatingFunnel.value = true;
    try {
      await store.dispatch('funnel/setSelectedFunnel', funnel);
      form.value.funnel_id = funnel.id;

      // Busca os stats das etapas do funil selecionado
      await store.dispatch('funnel/fetchStageStats', {
        funnelId: funnel.id,
        filterParams: {},
      });
    } catch (error) {
      console.error('Erro ao selecionar funil:', error);
    } finally {
      isUpdatingFunnel.value = false;
    }
  }
});

// Watch para atualizar o selectedFunnelId quando o selectedFunnel do store mudar
watch(
  selectedFunnel,
  newFunnel => {
    if (
      newFunnel?.id &&
      selectedFunnelId.value !== newFunnel.id &&
      !isUpdatingFunnel.value
    ) {
      selectedFunnelId.value = newFunnel.id;
    }
  },
  { immediate: true }
);

// Refs para controle do dropdown de agentes
const agentSearch = ref('');
const agentDropdownOpen = ref(false);
const agentSelectorRef = ref(null);
const filteredAgents = computed(() => {
  if (!agentSearch.value)
    return store.getters['agents/getAgents'].filter(
      a => !form.value.assigned_agents.some(sel => sel === a.id)
    );
  return store.getters['agents/getAgents'].filter(
    a =>
      !form.value.assigned_agents.some(sel => sel === a.id) &&
      a.name.toLowerCase().includes(agentSearch.value.toLowerCase())
  );
});

// Stage stats do funil (igual KanbanColumn: contagem por etapa)
const stageStats = computed(
  () => store.getters['funnel/getStageStats'](selectedFunnel.value?.id) || {}
);

// Computed para obter as etapas do funil selecionado (com count como em KanbanColumn)
const availableStages = computed(() => {
  if (!selectedFunnel.value?.stages) return [];

  return Object.entries(selectedFunnel.value.stages)
    .map(([id, stage]) => ({
      id,
      name: stage.name,
      position: stage.position,
      color: stage.color,
      count: stageStats.value[id]?.count ?? 0,
    }))
    .sort((a, b) => a.position - b.position);
});

// Estilo da borda e fundo do select de etapa (cor da etapa com opacidade no fundo)
const selectedStageBorderStyle = computed(() => {
  const stageId = form.value.funnel_stage;
  const color = selectedFunnel.value?.stages?.[stageId]?.color;
  if (!color) return {};
  const bgOpacity = '18'; // hex ~10% opacity
  const bgColor = color.length === 7 ? `${color}${bgOpacity}` : color;
  return {
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: color,
    backgroundColor: bgColor,
  };
});

// Refs para controle da conversa existente
const showConversationInput = ref(
  !!props.initialData?.item_details?.conversation_id
);
const conversations = ref([]);
const loadingConversations = ref(false);

// Refs para aba de Notas (quando isEditing)
const notesKanbanItems = ref([]);
const notesConversations = ref([]);
const notesContactsList = ref([]);
const notesLoadingItems = ref(false);
const notesLoadingConversations = ref(false);
const notesLoadingContacts = ref(false);

// Item e notas para a aba Notas (só faz sentido ao editar)
const itemForNotes = computed(() => {
  if (!props.isEditing || !props.initialData?.id) return null;
  return props.initialData;
});

const notesFromItem = computed(() => {
  const notes = props.initialData?.item_details?.notes;
  return Array.isArray(notes) ? notes : [];
});

// Notas em rascunho (antes de criar o item)
const draftNotes = ref([]);
const draftCurrentNote = ref('');
const editingDraftIndex = ref(null);

const addDraftNote = () => {
  const text = (draftCurrentNote.value || '').trim();
  if (!text) return;
  if (editingDraftIndex.value !== null) {
    draftNotes.value[editingDraftIndex.value] = {
      ...draftNotes.value[editingDraftIndex.value],
      text,
    };
    editingDraftIndex.value = null;
  } else {
    draftNotes.value.push({ tempId: Date.now(), text });
  }
  draftCurrentNote.value = '';
};

const removeDraftNote = index => {
  draftNotes.value.splice(index, 1);
  if (editingDraftIndex.value === index) {
    editingDraftIndex.value = null;
    draftCurrentNote.value = '';
  } else if (
    editingDraftIndex.value !== null &&
    editingDraftIndex.value > index
  ) {
    editingDraftIndex.value -= 1;
  }
};

const editDraftNote = index => {
  editingDraftIndex.value = index;
  draftCurrentNote.value = draftNotes.value[index]?.text || '';
};

const cancelEditDraftNote = () => {
  editingDraftIndex.value = null;
  draftCurrentNote.value = '';
};

const fetchNotesKanbanItems = async () => {
  try {
    notesLoadingItems.value = true;
    const response = await KanbanAPI.getItems();
    const data = response.data;
    const items = data?.items ?? (Array.isArray(data) ? data : []);
    notesKanbanItems.value = items.map(i => ({
      id: i.id,
      title: i.item_details?.title || t('KANBAN.UNTITLED_ITEM'),
      description: i.item_details?.description,
      value: i.item_details?.value,
      priority: i.item_details?.priority,
      stage: i.funnel_stage,
      stage_name: i.stageName,
      createdAt: i.created_at,
    }));
  } catch (err) {
    notesKanbanItems.value = [];
  } finally {
    notesLoadingItems.value = false;
  }
};

const fetchNotesConversations = async () => {
  try {
    notesLoadingConversations.value = true;
    const response = await conversationAPI.get({});
    const payload = response.data?.data?.payload ?? [];
    notesConversations.value = payload.map(c => ({
      id: c.id,
      title:
        c.meta?.sender?.name ||
        c.meta?.sender?.email ||
        t('KANBAN.ITEM_CONVERSATION.NO_CONTACT'),
      description: c.messages?.[0]?.content || t('KANBAN.NO_MESSAGES'),
      created_at: new Date(c.created_at).toLocaleDateString(),
      channel_type: c.channel_type,
      status: c.status,
    }));
  } catch (err) {
    notesConversations.value = [];
  } finally {
    notesLoadingConversations.value = false;
  }
};

const fetchNotesContacts = async () => {
  try {
    notesLoadingContacts.value = true;
    const response = await ContactAPI.get();
    notesContactsList.value = response.data?.payload ?? [];
  } catch (err) {
    notesContactsList.value = [];
  } finally {
    notesLoadingContacts.value = false;
  }
};

// Refs para criação de nova conversa (Relacionamentos)
const showNewConversationInput = ref(false);
const toggleRelationshipOption = option => {
  if (option === 'existing') {
    const nextValue = !showConversationInput.value;
    showConversationInput.value = nextValue;
    if (nextValue) {
      showNewConversationInput.value = false;
    }
    return;
  }

  const nextValue = !showNewConversationInput.value;
  showNewConversationInput.value = nextValue;
  if (nextValue) {
    showConversationInput.value = false;
  }
};
const newConversationContactSearch = ref('');
const newConversationContacts = ref([]);
const newConversationLoadingContacts = ref(false);
const newConversationSelectedContact = ref(null);
const newConversationInboxes = ref([]);
const newConversationLoadingInboxes = ref(false);
const newConversationSelectedInbox = ref(null);
const newConversationMessage = ref('');
const newConversationCreating = ref(false);
const messageTemplates = ref([]);
const loadingMessageTemplates = ref(false);
const selectedMessageTemplateId = ref(null);

// Refs para controle das ofertas
const availableOffers = ref([]);
const offerGroupsCatalog = ref([]);
const selectedOfferGroupFilter = ref('');
const loadingOffers = ref(false);
const showOffersInput = ref(!!props.initialData?.item_details?.offers?.length);
const showManualOfferForm = ref(false);
const manualOffer = ref({
  description: '',
  value: null,
  currency: { symbol: 'R$', code: 'BRL', locale: 'pt-BR' },
});
// Busca e filtro de conversas
const conversationSearch = ref('');
const conversationDropdownOpen = ref(false);
const conversationSelectorRef = ref(null);
const conversationDropdownStyle = ref({
  top: '0px',
  left: '0px',
  width: '100%',
});
const filteredConversations = computed(() => {
  if (!conversationSearch.value) return conversations.value;
  return conversations.value.filter(c => {
    const sender = c.meta?.sender;
    const text = `${c.id} ${sender?.name || ''} ${
      sender?.email || ''
    }`.toLowerCase();
    return text.includes(conversationSearch.value.toLowerCase());
  });
});
const selectedConversation = computed(() => {
  return (
    conversations.value.find(
      c => c.id === form.value.item_details.conversation_id
    ) || null
  );
});

const resetNewConversationState = () => {
  newConversationContactSearch.value = '';
  newConversationContacts.value = [];
  newConversationSelectedContact.value = null;
  newConversationInboxes.value = [];
  newConversationSelectedInbox.value = null;
  newConversationMessage.value = '';
  newConversationLoadingContacts.value = false;
  newConversationLoadingInboxes.value = false;
  newConversationCreating.value = false;
  messageTemplates.value = [];
  loadingMessageTemplates.value = false;
  selectedMessageTemplateId.value = null;
};

const loadInitialNewConversationContacts = async () => {
  newConversationLoadingContacts.value = true;
  try {
    const { data } = await ContactAPI.get(1);
    newConversationContacts.value = data.payload || [];
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: 'Erro ao carregar contatos',
      type: 'error',
    });
  } finally {
    newConversationLoadingContacts.value = false;
  }
};

const searchNewConversationContacts = async () => {
  if (!newConversationContactSearch.value) return;
  newConversationLoadingContacts.value = true;
  newConversationContacts.value = [];
  try {
    newConversationContacts.value = await searchContacts(
      newConversationContactSearch.value
    );
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: 'Erro ao buscar contatos',
      type: 'error',
    });
  } finally {
    newConversationLoadingContacts.value = false;
  }
};

const selectNewConversationContact = async contact => {
  newConversationSelectedContact.value = contact;
  newConversationInboxes.value = [];
  newConversationSelectedInbox.value = null;
  newConversationLoadingInboxes.value = true;
  try {
    const inboxes = await fetchContactableInboxes(contact.id);
    newConversationInboxes.value = inboxes;
    if (inboxes.length > 0) {
      newConversationSelectedInbox.value = inboxes[0];
    }
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: 'Erro ao carregar canais do contato',
      type: 'error',
    });
  } finally {
    newConversationLoadingInboxes.value = false;
  }
};

const loadMessageTemplatesForCurrentStage = async () => {
  const funnelId = form.value.funnel_id;
  const stageId = form.value.funnel_stage;
  if (!funnelId || !stageId) return;

  loadingMessageTemplates.value = true;
  messageTemplates.value = [];
  selectedMessageTemplateId.value = null;

  try {
    const response = await funnelAPI.getById(funnelId);
    const funnel = response.data;
    const stages = funnel.stages || {};
    const stage = stages[stageId];
    const templates = stage?.message_templates || [];
    messageTemplates.value = templates;
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: 'Erro ao carregar modelos de mensagem',
      type: 'error',
    });
  } finally {
    loadingMessageTemplates.value = false;
  }
};

const createNewConversationFromForm = async () => {
  if (
    !newConversationSelectedContact.value ||
    !newConversationSelectedInbox.value ||
    newConversationCreating.value
  )
    return;

  newConversationCreating.value = true;
  try {
    const baseMessage =
      newConversationMessage.value ||
      `Nova conversa iniciada para o item: ${
        form.value.title || `Item #${props.initialData?.id || ''}`
      }`;

    const payload = prepareNewMessagePayload({
      targetInbox: newConversationSelectedInbox.value,
      selectedContact: newConversationSelectedContact.value,
      message: baseMessage,
      subject: null,
      ccEmails: null,
      bccEmails: null,
      currentUser: currentUser.value,
      attachedFiles: [],
      directUploadsEnabled: false,
    });

    const isFromWhatsApp =
      newConversationSelectedInbox.value.channelType === 'Channel::Whatsapp';

    const data = await store.dispatch('contactConversations/create', {
      params: payload,
      isFromWhatsApp,
    });

    form.value.item_details.conversation_id = data.display_id || data.id;
    form.value.conversation_display_id = data.display_id || data.id;
    showConversationInput.value = true;

    // Garante que a conversa recém-criada apareça na lista local
    conversations.value = [...conversations.value, data];

    emitter.emit('newToastMessage', {
      message:
        'Conversa criada e associada ao item. Não esqueça de salvar o item.',
      type: 'success',
    });

    resetNewConversationState();
    showNewConversationInput.value = false;
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: 'Erro ao criar conversa',
      type: 'error',
    });
  } finally {
    newConversationCreating.value = false;
  }
};

// Atualizar a função para verificar se tem agendamento
const hasScheduling = computed(() => {
  return !!(
    props.initialData?.item_details?.scheduled_at ||
    props.initialData?.item_details?.deadline_at ||
    props.initialDate
  );
});

// Atualizar a ref showScheduling para usar o computed
const showScheduling = ref(hasScheduling.value);

// Forçar showScheduling a ser true se houver agendamento
// Isso garante que o checkbox esteja marcado
if (hasScheduling.value) {
  showScheduling.value = true;
}

// Atualizar o schedulingType para usar o tipo correto do item
const schedulingType = ref(
  props.initialData?.item_details?.scheduling_type ||
    (props.initialData?.item_details?.scheduled_at ? 'schedule' : 'deadline')
);

// Atualizar o form.value para incluir as ofertas e datas
const form = ref({
  title: props.initialData?.title || '',
  description:
    props.initialData?.item_details?.description ||
    props.initialData?.description ||
    '',
  funnel_id: props.funnelId,
  funnel_stage: props.stage,
  position: props.position,
  assigned_agents: extractAgentIds(props.initialData?.assigned_agents) || [],
  item_details: {
    title: props.initialData?.title || '',
    description:
      props.initialData?.item_details?.description ||
      props.initialData?.description ||
      '',
    value: null,
    offers: [],
    priority:
      props.initialData?.item_details?.priority ||
      props.initialData?.priority ||
      'none',
    conversation_id: props.initialData?.item_details?.conversation_id || null,
    scheduling_type: schedulingType.value,
    scheduled_at: null,
    deadline_at: null,
    closing_forecast_days:
      props.initialData?.item_details?.closing_forecast_days || null,
  },
  custom_attributes: props.initialData?.item_details?.custom_attributes || [],
});

watch(
  availableStages,
  stages => {
    if (!stages.length) return;

    const hasValidSelectedStage = stages.some(
      stage => stage.id === form.value.funnel_stage
    );

    if (!hasValidSelectedStage) {
      form.value.funnel_stage = stages[0].id;
    }
  },
  { immediate: true }
);

// Adicione estas refs
const customAttributes = ref(
  Array.isArray(props.initialData?.item_details?.custom_attributes)
    ? props.initialData.item_details.custom_attributes.map(attr => ({
        name: attr.name || '',
        type: attr.type || '',
      }))
    : Array.isArray(props.initialData?.custom_attributes)
      ? props.initialData.custom_attributes.map(attr => ({
          name: attr.name || '',
          type: attr.type || '',
        }))
      : []
);

// Campos globais do funil
const globalCustomAttributes = ref([]);
const globalCustomAttributesValues = ref({});

// Adicionar um watch para atualizar customAttributes ao trocar de item para edição
watch(
  () => [
    props.initialData?.item_details?.custom_attributes,
    props.initialData?.custom_attributes,
  ],
  ([newItemDetailsAttrs, newRootAttrs]) => {
    if (Array.isArray(newItemDetailsAttrs)) {
      customAttributes.value = newItemDetailsAttrs.map(attr => ({
        name: attr.name || '',
        type: attr.type || '',
      }));
    } else if (Array.isArray(newRootAttrs)) {
      customAttributes.value = newRootAttrs.map(attr => ({
        name: attr.name || '',
        type: attr.type || '',
      }));
    } else {
      customAttributes.value = [];
    }
  },
  { immediate: true, deep: true }
);

// Quando marcar "Criar nova conversa", carregar contatos/modelos e limpar seleção anterior
watch(showNewConversationInput, newVal => {
  if (newVal) {
    showConversationInput.value = false;
    form.value.item_details.conversation_id = null;
    form.value.conversation_display_id = null;
    if (
      !newConversationContacts.value.length &&
      !newConversationLoadingContacts.value
    ) {
      loadInitialNewConversationContacts();
    }
    if (!messageTemplates.value.length && !loadingMessageTemplates.value) {
      loadMessageTemplatesForCurrentStage();
    }
  } else {
    resetNewConversationState();
  }
});

// Modifique o onMounted para remover a busca de agentes
onMounted(async () => {
  // Carregar agentes do store
  loadingAgents.value = true;
  try {
    await store.dispatch('agents/get');

    // Selecionar automaticamente o usuário atual ao criar novo item
    if (!props.isEditing && form.value.assigned_agents.length === 0) {
      const currentUser = store.getters.getCurrentUser;
      if (currentUser?.id) {
        form.value.assigned_agents.push(currentUser.id);
      }
    }
  } catch (error) {
    // Error handling
  } finally {
    loadingAgents.value = false;
  }

  // Carregar funis do store
  loadingFunnels.value = true;
  try {
    await store.dispatch('funnel/fetch');

    // Inicializar o selectedFunnelId
    if (selectedFunnel.value?.id) {
      selectedFunnelId.value = selectedFunnel.value.id;
    } else if (availableFunnels.value.length > 0) {
      // Se não houver funil selecionado e existirem funis, seleciona o primeiro
      selectedFunnelId.value = availableFunnels.value[0].id;
    }

    // Se já há um funil selecionado, busca os stats dele
    if (selectedFunnel.value?.id) {
      await store.dispatch('funnel/fetchStageStats', {
        funnelId: selectedFunnel.value.id,
        filterParams: {},
      });
    }
  } catch (error) {
    console.error('Erro ao carregar funis:', error);
  } finally {
    loadingFunnels.value = false;
  }

  // Se tiver conversa atual ou estiver editando, buscar conversas
  if (
    props.currentConversation ||
    (props.isEditing && props.initialData?.item_details?.conversation_id)
  ) {
    showConversationInput.value = true;
    fetchConversations();
  }

  // Buscar ofertas e pacotes disponíveis
  fetchOffers();
  fetchOfferGroups();

  // Carregar campos globais do funil se estiver editando
  if (props.isEditing && props.initialData?.funnel_id) {
    loadGlobalAttributesForFunnel(props.initialData.funnel_id);
  }

  // Adicione este onMounted para gerenciar o clique fora do dropdown
  const handleClickOutside = event => {
    if (
      showCurrencySelector.value &&
      !event.target.closest('.currency-selector')
    ) {
      showCurrencySelector.value = false;
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  onUnmounted(() => {
    document.removeEventListener('mousedown', handleClickOutside);
  });

  // Processar ofertas existentes ao carregar o formulário em modo de edição
  if (props.isEditing && props.initialData?.item_details?.offers?.length) {
    form.value.item_details.offers = [...props.initialData.item_details.offers];
    const offers = form.value.item_details.offers;
    if (!offers.some(o => o.default === true)) offers[0].default = true;
  }

  // Sincronizar valores de agendamento
  if (hasScheduling.value) {
    showScheduling.value = true;

    if (props.initialData?.item_details?.scheduled_at) {
      schedulingType.value = 'schedule';
      form.value.item_details.scheduled_at = toLocalISOString(
        props.initialData.item_details.scheduled_at
      );
    } else if (props.initialData?.item_details?.deadline_at) {
      schedulingType.value = 'deadline';
      form.value.item_details.deadline_at = toLocalISOString(
        props.initialData.item_details.deadline_at,
        false // Apenas data para prazo
      );
    } else if (props.initialDate) {
      // Se houver uma data inicial, configure como agendamento com hora
      schedulingType.value = 'schedule';
      form.value.item_details.scheduled_at = toLocalISOString(
        props.initialDate,
        true // Data + hora para agendamento
      );
    }
  }
});

// Adicionar watch para o campo showScheduling
watch(showScheduling, newValue => {
  if (!newValue) {
    // Se o checkbox foi desmarcado, limpe os campos de agendamento
    form.value.item_details.scheduled_at = null;
    form.value.item_details.deadline_at = null;
  } else {
    // Se foi marcado mas não há tipo definido, defina um padrão
    if (!form.value.item_details.scheduling_type) {
      schedulingType.value = 'deadline';
      form.value.item_details.scheduling_type = 'deadline';
    }
  }
});

// Atualizar o watch do schedulingType
watch(schedulingType, (newType, oldType) => {
  if (newType === oldType) return;

  form.value.item_details.scheduling_type = newType;

  if (newType === 'deadline') {
    // Se mudar para deadline, preserva a data existente ou converte do scheduled_at
    if (
      !form.value.item_details.deadline_at &&
      form.value.item_details.scheduled_at
    ) {
      // Converter para o formato de date
      form.value.item_details.deadline_at = toLocalISOString(
        form.value.item_details.scheduled_at,
        false // Sem hora
      );
    }
    form.value.item_details.scheduled_at = null;
  } else {
    // Se mudar para schedule, preserva a data existente ou converte do deadline_at
    if (
      !form.value.item_details.scheduled_at &&
      form.value.item_details.deadline_at
    ) {
      // Converter para o formato de datetime-local
      const deadlineDate = new Date(form.value.item_details.deadline_at);
      // Define hora como meio-dia por padrão
      deadlineDate.setHours(12, 0, 0, 0);
      form.value.item_details.scheduled_at = toLocalISOString(deadlineDate);
    }
    form.value.item_details.deadline_at = null;
  }
});

// Função para buscar conversas (todas as páginas do endpoint GET /api/v1/accounts/:id/conversations)
const fetchConversations = async () => {
  try {
    loadingConversations.value = true;
    if (props.currentConversation) {
      conversations.value = [props.currentConversation];
    } else {
      const all = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const response = await conversationAPI.get({ page });
        const payload = response.data?.data?.payload ?? [];
        all.push(...payload);
        hasMore = payload.length >= 25;
        page += 1;
      }
      conversations.value = all;
    }
  } catch (error) {
    // Error handling
  } finally {
    loadingConversations.value = false;
  }
};

// Função para buscar ofertas disponíveis
const fetchOffers = async () => {
  try {
    loadingOffers.value = true;
    const response = await offersAPI.getOffers();
    availableOffers.value = (response.data || []).map(offer => ({
      id: offer.id,
      name: offer.title,
      discount_percentage: offer.value,
      currency: offer.currency,
      type: offer.type,
      product_link: offer.product_link,
      created_at: offer.created_at,
      image_url: offer.image_url,
      offer_group_id:
        offer.offer_group_id != null && offer.offer_group_id !== ''
          ? offer.offer_group_id
          : null,
      offer_group_name: offer.offer_group_name || '',
    }));
  } catch (error) {
    console.error('Erro ao carregar ofertas:', error);
    availableOffers.value = [];
  } finally {
    loadingOffers.value = false;
  }
};

const fetchOfferGroups = async () => {
  try {
    const { data } = await offerGroupsAPI.list();
    offerGroupsCatalog.value = Array.isArray(data) ? data : [];
  } catch {
    offerGroupsCatalog.value = [];
  }
};

// Função para carregar atributos globais de um funil específico
const loadGlobalAttributesForFunnel = async funnelId => {
  try {
    const response = await funnelAPI.get();
    const funnels = response.data || [];
    const selectedFunnelData = funnels.find(f => f.id === funnelId);

    // Inicializar campos globais do funil
    if (selectedFunnelData?.global_custom_attributes?.length > 0) {
      globalCustomAttributes.value = [
        ...selectedFunnelData.global_custom_attributes,
      ];

      // Buscar valores existentes dos custom_attributes (backend pode retornar array ou {})
      const rawAttrs = props.initialData?.item_details?.custom_attributes;
      const existingCustomAttrs = Array.isArray(rawAttrs) ? rawAttrs : [];

      // Inicializar valores dos campos globais com dados existentes
      const existingGlobalValues = {};
      selectedFunnelData.global_custom_attributes.forEach(attr => {
        const existingAttr = existingCustomAttrs.find(
          ca => ca.name === attr.name
        );
        if (existingAttr) {
          existingGlobalValues[attr.name] = existingAttr.value;
        } else if (attr.is_list) {
          existingGlobalValues[attr.name] = [''];
        } else {
          existingGlobalValues[attr.name] = '';
        }
      });
      globalCustomAttributesValues.value = existingGlobalValues;
    } else {
      globalCustomAttributes.value = [];
      globalCustomAttributesValues.value = {};
    }
  } catch (error) {
    console.error('Erro ao carregar atributos globais do funil:', error);
    globalCustomAttributes.value = [];
    globalCustomAttributesValues.value = {};
  }
};

watch(showConversationInput, newValue => {
  if (!newValue) {
    form.value.item_details.conversation_id = null;
  } else {
    fetchConversations();
  }
});

watch(showOffersInput, newValue => {
  if (!newValue) {
    form.value.item_details.offers = [];
    form.value.item_details.value = null;
    selectedOfferGroupFilter.value = '';
  }
});

// Funções para gerenciar ofertas
const isOfferSelected = offerId => {
  return form.value.item_details.offers.some(offer => {
    if (offer.id) return offer.id === offerId;
    if (offer.offer_id) return offer.offer_id === offerId;
    return false;
  });
};

// Verifica se uma oferta do catálogo já está na lista selecionada (por id ou por valor/descrição/moeda)
const isCatalogOfferInSelectedList = catalogOffer => {
  const selected = form.value.item_details.offers;
  return selected.some(sel => {
    if (sel.id != null || sel.offer_id != null) {
      return (sel.id ?? sel.offer_id) === catalogOffer.id;
    }
    const code = sel.currency?.code ?? sel.currency;
    const catalogCode =
      typeof catalogOffer.currency === 'string'
        ? catalogOffer.currency
        : catalogOffer.currency?.code;
    const sameValue =
      String(sel.value) === String(catalogOffer.discount_percentage);
    const sameDesc =
      (sel.description || '').trim() === (catalogOffer.name || '').trim();
    const sameCurrency = (code || '') === (catalogCode || '');
    return sameValue && sameDesc && sameCurrency;
  });
};

const offersMatchingPackageFilter = computed(() => {
  if (!selectedOfferGroupFilter.value) return availableOffers.value;
  return availableOffers.value.filter(
    o =>
      String(o.offer_group_id ?? '') === String(selectedOfferGroupFilter.value)
  );
});

// Ofertas ainda não selecionadas (removidas da listagem selecionável), após filtro de pacote
const selectableOffers = computed(() =>
  offersMatchingPackageFilter.value.filter(
    offer => !isCatalogOfferInSelectedList(offer)
  )
);

// Oferta que define o value do item: a marcada como padrão ou a primeira
const defaultOfferIndex = computed(() => {
  const offers = form.value.item_details.offers;
  if (!offers?.length) return -1;
  const idx = offers.findIndex(o => o.default === true);
  return idx >= 0 ? idx : 0;
});

const defaultOffer = computed(() => {
  const offers = form.value.item_details.offers;
  const idx = defaultOfferIndex.value;
  return idx >= 0 && offers?.[idx] ? offers[idx] : null;
});

const syncValueFromDefaultOffer = () => {
  const offer = defaultOffer.value;
  if (offer) {
    form.value.item_details.value = offer.value;
    form.value.item_details.currency = offer.currency;
  } else {
    form.value.item_details.value = null;
    form.value.item_details.currency = null;
  }
};

const setDefaultOffer = index => {
  form.value.item_details.offers.forEach((o, i) => {
    o.default = i === index;
  });
  syncValueFromDefaultOffer();
};

const onDefaultOfferCheckboxChange = (index, checked) => {
  if (checked) {
    setDefaultOffer(index);
  } else {
    const other = form.value.item_details.offers.findIndex(
      (_, i) => i !== index
    );
    setDefaultOffer(other >= 0 ? other : 0);
  }
};

const appendCatalogOffer = offer => {
  const wasEmpty = form.value.item_details.offers.length === 0;
  form.value.item_details.offers.push({
    id: offer.id,
    description: offer.name,
    value: offer.discount_percentage,
    product_link: offer.product_link,
    image_url: offer.image_url,
    default: wasEmpty,
    manual: false,
    offer_group_id:
      offer.offer_group_id != null && offer.offer_group_id !== ''
        ? offer.offer_group_id
        : null,
    offer_group_name: offer.offer_group_name || '',
    currency: {
      symbol:
        offer.currency === 'BRL' ? 'R$' : offer.currency === 'USD' ? '$' : '€',
      code: offer.currency,
      locale:
        offer.currency === 'BRL'
          ? 'pt-BR'
          : offer.currency === 'USD'
            ? 'en-US'
            : 'de-DE',
    },
  });
  if (!wasEmpty) {
    form.value.item_details.offers.forEach(o => {
      if (o.default === undefined) o.default = false;
    });
  }
};

const syncOffersSelectionAfterChange = () => {
  if (form.value.item_details.offers.length > 0) {
    if (!form.value.item_details.offers.some(o => o.default === true))
      form.value.item_details.offers[0].default = true;
    syncValueFromDefaultOffer();
  } else {
    form.value.item_details.value = null;
    form.value.item_details.currency = null;
  }
};

const toggleOfferSelection = offer => {
  const offerIndex = form.value.item_details.offers.findIndex(o => {
    if (o.id) return o.id === offer.id;
    if (o.offer_id) return o.offer_id === offer.id;
    return false;
  });

  if (offerIndex !== -1) {
    form.value.item_details.offers.splice(offerIndex, 1);
  } else {
    appendCatalogOffer(offer);
  }

  syncOffersSelectionAfterChange();
};

const selectAllFilteredOffers = () => {
  selectableOffers.value.forEach(offer => {
    if (!isOfferSelected(offer.id)) {
      appendCatalogOffer(offer);
    }
  });
  syncOffersSelectionAfterChange();
};

const addManualOffer = () => {
  if (!manualOffer.value.description || !manualOffer.value.value) {
    emitter.emit('newToastMessage', {
      message: 'Preencha descrição e valor da oferta',
      type: 'error',
    });
    return;
  }

  const wasEmpty = form.value.item_details.offers.length === 0;
  form.value.item_details.offers.push({
    manual: true,
    description: manualOffer.value.description,
    value: parseKanbanCurrencyInput(manualOffer.value.value) ?? 0,
    currency: manualOffer.value.currency,
    default: wasEmpty,
  });
  if (!wasEmpty)
    form.value.item_details.offers.forEach((o, i) => {
      if (o.default === undefined) o.default = false;
    });

  if (form.value.item_details.offers.length > 0) {
    if (!form.value.item_details.offers.some(o => o.default === true))
      form.value.item_details.offers[0].default = true;
    syncValueFromDefaultOffer();
  }

  // Resetar o formulário
  manualOffer.value = {
    description: '',
    value: null,
    currency: { symbol: 'R$', code: 'BRL', locale: 'pt-BR' },
  };
  showManualOfferForm.value = false;

  emitter.emit('newToastMessage', {
    message: 'Oferta adicionada com sucesso',
    type: 'success',
  });
};

const cancelManualOffer = () => {
  manualOffer.value = {
    description: '',
    value: null,
    currency: { symbol: 'R$', code: 'BRL', locale: 'pt-BR' },
  };
  showManualOfferForm.value = false;
};

const removeOffer = index => {
  const wasDefault = form.value.item_details.offers[index]?.default === true;
  form.value.item_details.offers.splice(index, 1);
  if (form.value.item_details.offers.length > 0) {
    if (
      wasDefault &&
      !form.value.item_details.offers.some(o => o.default === true)
    )
      form.value.item_details.offers[0].default = true;
    syncValueFromDefaultOffer();
  } else {
    form.value.item_details.value = null;
    form.value.item_details.currency = null;
  }
};

// Atualiza o form quando o funil ou etapa mudar
watchEffect(() => {
  form.value = {
    ...form.value,
    funnel_id: selectedFunnel.value?.id || props.funnelId,
    funnel_stage: form.value.funnel_stage || props.stage,
    position: props.position,
    assigned_agents:
      form.value.assigned_agents ||
      extractAgentIds(props.initialData?.assigned_agents) ||
      [],
    title:
      form.value.title ||
      props.initialData?.title ||
      props.initialData?.item_details?.title ||
      '',
    item_details: {
      ...form.value.item_details,
      title:
        form.value.title ||
        props.initialData?.title ||
        props.initialData?.item_details?.title ||
        '',
      description: form.value.description,
      priority:
        form.value.item_details.priority ||
        props.initialData?.item_details?.priority ||
        props.initialData?.priority ||
        'none',
      value:
        form.value.item_details.value || props.initialData?.item_details?.value,
      currency:
        form.value.item_details.currency ||
        props.initialData?.item_details?.currency,
      scheduled_at:
        form.value.item_details.scheduled_at ||
        props.initialData?.item_details?.scheduled_at,
      deadline_at:
        form.value.item_details.deadline_at ||
        props.initialData?.item_details?.deadline_at,
    },
  };
});

// Adicionar watch para sincronizar o título em tempo real
watch(
  () => form.value.title,
  newTitle => {
    if (form.value.item_details) {
      form.value.item_details.title = newTitle;
    }
  }
);

// Adicionar watch para sincronizar a descrição em tempo real
watch(
  () => form.value.description,
  newDescription => {
    if (form.value.item_details) {
      form.value.item_details.description = newDescription;
    }
  }
);

// Adicionar watch para sincronizar assigned_agents quando initialData mudar
watch(
  () => props.initialData?.assigned_agents,
  newAssignedAgents => {
    if (Array.isArray(newAssignedAgents)) {
      form.value.assigned_agents = extractAgentIds(newAssignedAgents);
    }
  },
  { immediate: true, deep: true }
);

// Watch para sincronizar ofertas ao abrir para edição (recuperar e marcar na aba ofertas)
watch(
  () => props.initialData?.item_details?.offers,
  newOffers => {
    if (!props.isEditing) return;
    if (Array.isArray(newOffers)) {
      form.value.item_details.offers = newOffers.map(o => ({
        ...o,
        manual: o.manual ?? true,
      }));
      const offers = form.value.item_details.offers;
      if (offers.length > 0 && !offers.some(o => o.default === true))
        offers[0].default = true;
      showOffersInput.value = newOffers.length > 0;
    }
  },
  { immediate: true, deep: true }
);

// Adicionar watch para atualizar valores globais quando o funil mudar
watch(
  () => selectedFunnel.value?.id,
  async newFunnelId => {
    // Só atualizar se não estiver editando (quando editando, usamos o funnel_id do item)
    if (
      newFunnelId &&
      activeTab.value === 'custom_fields' &&
      !props.isEditing
    ) {
      try {
        const response = await funnelAPI.get();
        const funnels = response.data || [];
        const selectedFunnelData = funnels.find(f => f.id === newFunnelId);

        // Atualizar campos globais quando o funil mudar
        if (selectedFunnelData?.global_custom_attributes?.length > 0) {
          globalCustomAttributes.value = [
            ...selectedFunnelData.global_custom_attributes,
          ];

          // Preservar valores existentes ou inicializar vazios
          const existingGlobalValues = {
            ...globalCustomAttributesValues.value,
          };
          selectedFunnelData.global_custom_attributes.forEach(attr => {
            if (!(attr.name in existingGlobalValues)) {
              if (attr.is_list) {
                existingGlobalValues[attr.name] = [''];
              } else {
                existingGlobalValues[attr.name] = '';
              }
            }
          });
          globalCustomAttributesValues.value = existingGlobalValues;
        } else {
          globalCustomAttributes.value = [];
          globalCustomAttributesValues.value = {};
        }
      } catch (error) {
        console.error('Erro ao atualizar campos globais:', error);
      }
    }
  },
  { immediate: true }
);

// Modificar a função validateForm para incluir a validação de data
const validateForm = () => {
  const errors = {};

  if (!form.value.title?.trim()) {
    errors.title = t('KANBAN.ERRORS.TITLE_REQUIRED');
  }

  if (!form.value.funnel_id) {
    errors.funnel_id = t('KANBAN.ERRORS.FUNNEL_REQUIRED');
  }

  if (!form.value.funnel_stage) {
    errors.funnel_stage = t('KANBAN.ERRORS.STAGE_REQUIRED');
  }

  // Removidas todas as validações de data/prazo

  if (Object.keys(errors).length > 0) {
    emitter.emit('newToastMessage', {
      message: errors[Object.keys(errors)[0]],
      type: 'error',
    });
    return errors;
  }

  return null;
};

const priorityOptions = [
  { id: 'none', name: t('KANBAN.PRIORITY_LABELS.NONE') },
  { id: 'low', name: t('KANBAN.PRIORITY_LABELS.LOW') },
  { id: 'medium', name: t('KANBAN.PRIORITY_LABELS.MEDIUM') },
  { id: 'high', name: t('KANBAN.PRIORITY_LABELS.HIGH') },
  { id: 'urgent', name: t('KANBAN.PRIORITY_LABELS.URGENT') },
];

// Função para obter estilos de prioridade com cores hexadecimais
const getPriorityStyle = optionId => {
  const isSelected = optionId === form.value.item_details.priority;

  const styles = {
    none: {
      selected: {
        borderColor: '#e2e8f0',
        backgroundColor: '#f1f5f9',
        color: '#475569',
      },
      unselected: {
        borderColor: '#e2e8f0',
        backgroundColor: '#f1f5f9',
        color: '#64748b',
      },
    },
    urgent: {
      selected: {
        borderColor: '#fecdd3',
        backgroundColor: '#e11d48',
        color: '#ffffff',
      },
      unselected: {
        borderColor: '#fecdd3',
        backgroundColor: '#ffe4e6',
        color: '#e11d48',
      },
    },
    high: {
      selected: {
        borderColor: '#fed7aa',
        backgroundColor: '#ea580c',
        color: '#ffffff',
      },
      unselected: {
        borderColor: '#fed7aa',
        backgroundColor: '#ffedd5',
        color: '#ea580c',
      },
    },
    medium: {
      selected: {
        borderColor: '#fef08a',
        backgroundColor: '#eab308',
        color: '#ffffff',
      },
      unselected: {
        borderColor: '#fef08a',
        backgroundColor: '#fef9c3',
        color: '#996b00',
      },
    },
    low: {
      selected: {
        borderColor: '#bbf7d0',
        backgroundColor: '#22c55e',
        color: '#ffffff',
      },
      unselected: {
        borderColor: '#bbf7d0',
        backgroundColor: '#dcfce7',
        color: '#16a34a',
      },
    },
  };

  const styleConfig =
    styles[optionId]?.[isSelected ? 'selected' : 'unselected'] ||
    styles.none.unselected;

  return {
    borderColor: styleConfig.borderColor,
    backgroundColor: styleConfig.backgroundColor,
    color: styleConfig.color,
  };
};

// Função auxiliar para registrar atividades
const registerActivity = async (itemId, type, details) => {
  try {
    await store.dispatch('kanban/registerActivity', {
      itemId,
      type,
      details,
    });
  } catch (error) {
    // Error handling
  }
};

// Modificar handleSubmit para não usar agentsList
const handleSubmit = async e => {
  e.preventDefault();
  const errors = validateForm();
  if (errors) return;

  try {
    loading.value = true;

    // Se o usuário marcou para criar nova conversa, cria automaticamente antes de salvar o item
    if (showNewConversationInput.value) {
      await createNewConversationFromForm();
    }

    // Garantir que título e descrição estejam sincronizados
    form.value.item_details.title = form.value.title;
    form.value.item_details.description = form.value.description;

    let finalItemDetails = {};

    // Se estiver editando, preservar os dados originais do item
    if (props.isEditing && props.initialData?.item_details) {
      finalItemDetails = JSON.parse(
        JSON.stringify(props.initialData.item_details)
      );

      const fieldsToPreserve = ['checklist', 'attachments', 'activities'];
      fieldsToPreserve.forEach(field => {
        if (props.initialData.item_details[field]) {
          finalItemDetails[field] = props.initialData.item_details[field];
        }
      });

      // Não enviar notes no update: notas são geridas pela API de notas (create_note, etc.).
      // O backend preserva as notas atuais no update para não sobrescrever as adicionadas no form.
      delete finalItemDetails.notes;
    }

    const fieldsToUpdate = [
      'title',
      'description',
      'value',
      'currency',
      'priority',
      'conversation_id',
      'scheduling_type',
      'scheduled_at',
      'deadline_at',
      'closing_forecast_days',
      'custom_attributes',
    ];

    fieldsToUpdate.forEach(field => {
      if (form.value.item_details[field] !== undefined) {
        finalItemDetails[field] = form.value.item_details[field];
      }
    });

    finalItemDetails.title = form.value.title;
    finalItemDetails.offers = form.value.item_details.offers;

    const offerForValue = defaultOffer.value;
    if (offerForValue) {
      finalItemDetails.value = offerForValue.value;
      finalItemDetails.currency = offerForValue.currency;
    }

    // Criar o payload completo
    const payload = {
      funnel_id: selectedFunnel.value?.id || props.funnelId,
      funnel_stage: form.value.funnel_stage,
      position: props.position,
      assigned_agents: form.value.assigned_agents,
      item_details: finalItemDetails,
      conversation_display_id: form.value.conversation_display_id,
    };

    // custom_attributes deve ser um array de objetos {name, type, value}
    const existingCustomAttributes = Array.isArray(
      finalItemDetails.custom_attributes
    )
      ? finalItemDetails.custom_attributes
      : [];

    // Atualizar custom_attributes com os valores globais preenchidos
    let updatedCustomAttributes = existingCustomAttributes.map(attr => {
      // Se o campo global foi preenchido, atualizar seu valor
      if (globalCustomAttributesValues.value[attr.name] !== undefined) {
        const newValue = globalCustomAttributesValues.value[attr.name];

        // Filtrar arrays vazios
        if (Array.isArray(newValue)) {
          const filteredArray = newValue.filter(
            v => v !== null && v !== undefined && v !== ''
          );
          return { ...attr, value: filteredArray };
        }

        // Para valores simples
        return { ...attr, value: newValue };
      }

      // Manter o atributo sem alteração
      return attr;
    });

    // Incluir atributos globais do funil que tenham valor preenchido e ainda não estejam no array
    const namesInPayload = new Set(updatedCustomAttributes.map(a => a.name));
    globalCustomAttributes.value.forEach(attr => {
      if (namesInPayload.has(attr.name)) return;
      const value = globalCustomAttributesValues.value[attr.name];
      if (value === undefined) return;
      const toAdd = Array.isArray(value)
        ? value.filter(v => v !== null && v !== undefined && v !== '')
        : value;
      if (
        Array.isArray(toAdd) ? toAdd.length > 0 : toAdd !== '' && toAdd != null
      ) {
        updatedCustomAttributes.push({
          name: attr.name,
          type: attr.type || 'text',
          value: toAdd,
        });
        namesInPayload.add(attr.name);
      }
    });

    finalItemDetails.custom_attributes = updatedCustomAttributes;

    const result = props.isEditing
      ? await store.dispatch('kanban/updateItemDetails', {
          itemId: props.initialData.id,
          details: payload, // Enviar o payload completo com funnel_stage
        })
      : await store.dispatch('kanban/createKanbanItem', payload);

    if (!props.isEditing && result?.id && draftNotes.value.length > 0) {
      for (const note of draftNotes.value) {
        await KanbanAPI.createNote(result.id, {
          text: note.text,
          attachments: [],
          linked_item_id: note.linked_item_id || null,
          linked_conversation_id: note.linked_conversation_id || null,
          linked_contact_id: note.linked_contact_id || null,
        });
      }
      draftNotes.value = [];
    }

    emitter.emit('newToastMessage', {
      message: props.isEditing
        ? 'Item atualizado com sucesso'
        : 'Item criado com sucesso',
      type: 'success',
    });

    emit('saved', result);
    emit('close');
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: 'Erro ao salvar item',
      type: 'error',
    });
  } finally {
    loading.value = false;
  }
};

// Adicione esta função no script
const formatCurrencyValue = (value, currency) => {
  if (!value) return '-';

  try {
    return new Intl.NumberFormat(currency.locale, {
      style: 'currency',
      currency: currency.code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (error) {
    return value.toString();
  }
};

const businessValueMask = computed({
  get() {
    const v = form.value?.item_details?.value;
    if (v === null || v === undefined || v === '') return '';
    return formatKanbanCurrency(v);
  },
  set(raw) {
    const masked = maskKanbanCurrencyTyping(raw);
    form.value.item_details.value = parseKanbanCurrencyInput(masked);
  },
});

const onBusinessValueInput = event => {
  const masked = maskKanbanCurrencyTyping(event?.target?.value);
  if (event?.target) event.target.value = masked;
  form.value.item_details.value = parseKanbanCurrencyInput(masked);
};

const onManualOfferValueInput = event => {
  const masked = maskKanbanCurrencyTyping(event?.target?.value);
  if (event?.target) event.target.value = masked;
  manualOffer.value.value = masked;
};

const { isStacklab } = useConfig();

// ===== SiteUp V2 (2026-08-11): CRIAR é uma tela só =====
// Mockup aprovado: https://claude.ai/code/artifact/fbd8786f-910f-4258-890a-dff80f834b8b
// As 8 abas existem para EDITAR um card que já tem tudo. Para pôr um lead no
// funil, quem cria precisa de cinco campos — e principalmente precisa VER em
// que etapa o card vai nascer, que antes nunca era perguntado.
const createFunnelMenuOpen = ref(false);
const createStageMenuOpen = ref(false);
const showMoreCreateFields = ref(false);

const createContact = computed(
  () => props.currentConversation?.meta?.sender || null
);

const createFunnelName = computed(
  () =>
    availableFunnelsForSelector.value.find(
      f => String(f.id) === String(selectedFunnelId.value)
    )?.name || t('KANBAN.SELECT_FUNNEL')
);

const createStageIdx = computed(() =>
  availableStages.value.findIndex(
    s => String(s.id) === String(form.value.funnel_stage)
  )
);

const createStage = computed(
  () => availableStages.value[createStageIdx.value] || null
);

const toggleCreateFunnelMenu = () => {
  createFunnelMenuOpen.value = !createFunnelMenuOpen.value;
  if (createFunnelMenuOpen.value) createStageMenuOpen.value = false;
};

const toggleCreateStageMenu = () => {
  createStageMenuOpen.value = !createStageMenuOpen.value;
  if (createStageMenuOpen.value) createFunnelMenuOpen.value = false;
};

// Trocar de funil reabre o menu de ETAPAS do funil novo, já na primeira: a
// etapa herdada do funil anterior não existe aqui, e escolher em silêncio é
// justamente o que fazia todo card nascer na primeira etapa.
const pickCreateFunnel = funnel => {
  selectedFunnelId.value = funnel.id;
  createFunnelMenuOpen.value = false;
  nextTick(() => {
    form.value.funnel_stage = availableStages.value[0]?.id || '';
    if (availableStages.value.length > 1) createStageMenuOpen.value = true;
  });
};

const pickCreateStage = stage => {
  form.value.funnel_stage = stage.id;
  createStageMenuOpen.value = false;
};

const offersTabLabel = computed(
  () => store.getters['kanban/getOffersTabTitle'] || t('KANBAN.TABS.OFFERS')
);

// Função para copiar o ID do item
const copyItemId = async () => {
  if (!props.initialData?.id) return;

  try {
    await navigator.clipboard.writeText(props.initialData.id.toString());
    emitter.emit('newToastMessage', {
      message: t('KANBAN.ITEM_ID_COPIED') || 'ID do item copiado!',
      type: 'success',
    });
  } catch (error) {
    // Fallback para browsers antigos
    const textArea = document.createElement('textarea');
    textArea.value = props.initialData.id.toString();
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);

    emitter.emit('newToastMessage', {
      message: t('KANBAN.ITEM_ID_COPIED') || 'ID do item copiado!',
      type: 'success',
    });
  }
};

// Dados das condições de status
// Dados mock das ações (mantendo compatibilidade)
// Funções para gerenciar agentes
const addAgent = agent => {
  if (!form.value.assigned_agents.some(a => a === agent.id))
    form.value.assigned_agents.push(agent.id);
  agentDropdownOpen.value = false;
  agentSearch.value = '';
};

const removeAgent = agentId => {
  form.value.assigned_agents = form.value.assigned_agents.filter(
    a => a !== agentId
  );
};

// Funções para gerenciar campos globais de lista
const addGlobalListItem = fieldName => {
  if (!globalCustomAttributesValues.value[fieldName]) {
    globalCustomAttributesValues.value[fieldName] = [];
  }
  globalCustomAttributesValues.value[fieldName].push('');
};

const removeGlobalListItem = (fieldName, index) => {
  if (
    globalCustomAttributesValues.value[fieldName] &&
    globalCustomAttributesValues.value[fieldName].length > 1
  ) {
    globalCustomAttributesValues.value[fieldName].splice(index, 1);
  }
};

const getAgentById = agentId => {
  return store.getters['agents/getAgents'].find(a => a.id === agentId);
};

const openAgentDropdown = () => {
  agentDropdownOpen.value = true;
  agentSearch.value = '';
};

const closeAgentDropdown = e => {
  if (!agentSelectorRef.value?.contains(e.target)) {
    agentDropdownOpen.value = false;
  }
};

const addCustomAttribute = () => {
  customAttributes.value.push({ name: '', type: '' });
};

function removeCustomAttribute(index) {
  customAttributes.value.splice(index, 1);
}

// No script, garanta que customAttributes sempre seja um array e, se vazio, adicione pelo menos um campo ao entrar na aba:
watch(activeTab, async tab => {
  if (tab === 'notes') {
    if (notesKanbanItems.value.length === 0) fetchNotesKanbanItems();
    if (notesConversations.value.length === 0) fetchNotesConversations();
    if (notesContactsList.value.length === 0) fetchNotesContacts();
  }
  if (tab === 'custom_fields') {
    // Fazer get para buscar dados do funnel quando abrir a aba de dados adicionais
    try {
      const response = await funnelAPI.get();
      const funnels = response.data || [];

      // Identificar o funil correto: usar o funnel_id do item original se estiver editando, senão usar o selectedFunnel
      const currentFunnelId = props.isEditing
        ? props.initialData?.funnel_id || props.funnelId
        : selectedFunnel.value?.id || props.funnelId;

      // Encontrar o funil correto
      const selectedFunnelData = funnels.find(f => f.id === currentFunnelId);

      // Inicializar campos globais do funil
      if (selectedFunnelData?.global_custom_attributes?.length > 0) {
        globalCustomAttributes.value = [
          ...selectedFunnelData.global_custom_attributes,
        ];

        // Buscar valores existentes dos custom_attributes (backend pode retornar array ou {})
        const rawAttrs = props.initialData?.item_details?.custom_attributes;
        const existingCustomAttrs = Array.isArray(rawAttrs) ? rawAttrs : [];

        // Inicializar valores dos campos globais com dados existentes
        const existingGlobalValues = {};
        selectedFunnelData.global_custom_attributes.forEach(attr => {
          const existingAttr = existingCustomAttrs.find(
            ca => ca.name === attr.name
          );
          if (existingAttr) {
            // Usar o valor existente
            existingGlobalValues[attr.name] = existingAttr.value;
          } else if (attr.is_list) {
            // Para campos de lista novos, inicializar como array com um item vazio
            existingGlobalValues[attr.name] = [''];
          } else {
            // Para campos simples novos, inicializar vazio
            existingGlobalValues[attr.name] = '';
          }
        });
        globalCustomAttributesValues.value = existingGlobalValues;
      } else {
        globalCustomAttributes.value = [];
        globalCustomAttributesValues.value = {};
      }

      // Inicializar customAttributes apenas com campos adicionais (não globais)
      if (customAttributes.value.length === 0) {
        customAttributes.value.push({ name: '', type: '' });
      }
    } catch (error) {
      console.error('Erro ao buscar dados do funnel:', error);
      // Fallback para comportamento anterior se a API falhar
      if (customAttributes.value.length === 0) {
        customAttributes.value.push({ name: '', type: '' });
      }
    }
  }
});

function openConversationDropdown() {
  conversationDropdownOpen.value = true;
  conversationSearch.value = '';
  setTimeout(() => {
    const selector = conversationSelectorRef.value;
    if (selector) {
      const rect = selector.getBoundingClientRect();
      conversationDropdownStyle.value = {
        position: 'fixed',
        top: `${rect.bottom + window.scrollY}px`,
        left: `${rect.left + window.scrollX}px`,
        width: `${rect.width}px`,
        zIndex: 99999,
      };
    }
    const input = selector?.querySelector('input');
    if (input) input.focus();
  }, 0);
}
function selectConversation(id) {
  form.value.item_details.conversation_id = id;
  form.value.conversation_display_id = id;
  conversationDropdownOpen.value = false;
}
function closeConversationDropdown(e) {
  if (!conversationSelectorRef.value?.contains(e.target)) {
    conversationDropdownOpen.value = false;
  }
}
onMounted(() => {
  document.addEventListener('mousedown', closeConversationDropdown);
  document.addEventListener('mousedown', closeAgentDropdown);
});
onUnmounted(() => {
  document.removeEventListener('mousedown', closeConversationDropdown);
  document.removeEventListener('mousedown', closeAgentDropdown);
});
</script>

<template>
  <form
    class="flex flex-col gap-4 max-h-[85vh] overflow-y-auto w-full px-4"
    @submit.prevent="handleSubmit"
  >

    <!-- ===== SiteUp V2 (2026-08-11): CRIAR = tela única, sem abas =====
         Mockup: https://claude.ai/code/artifact/fbd8786f-910f-4258-890a-dff80f834b8b
         Cinco campos. Prazo, previsão e campos do funil ficam atrás do "＋":
         existem, mas não pedem atenção de quem só quer pôr o lead no funil. -->
    <div
      v-if="!isEditing"
      data-testid="kanban-create-screen"
      class="flex flex-col gap-5"
    >
      <!-- Contato: vem da conversa, então é cabeçalho e não campo. -->
      <div
        v-if="createContact"
        data-testid="kanban-create-contact"
        class="flex items-center gap-3 rounded-2xl border border-n-weak bg-n-solid-2 p-3"
      >
        <span
          class="flex size-9 shrink-0 items-center justify-center rounded-full bg-n-solid-3 text-sm font-bold text-n-slate-12"
        >
          {{ (createContact.name || '?').charAt(0).toUpperCase() }}
        </span>
        <div class="min-w-0">
          <p class="m-0 truncate text-sm font-medium text-n-slate-12">
            {{ createContact.name }}
          </p>
          <p class="m-0 truncate text-xs text-n-slate-11">
            {{ createContact.phone_number || createContact.email || '' }}
          </p>
        </div>
      </div>

      <!-- Onde entra: funil e etapa como seletores irmãos. A etapa é a razão
           de existir desta tela — antes o card nascia na primeira sem avisar. -->
      <div class="flex flex-col gap-2">
        <label class="text-xs font-bold uppercase tracking-wide text-n-slate-11">
          {{ t('KANBAN.FORM.WHERE_IT_GOES') }}
        </label>
        <div class="flex flex-wrap items-center gap-2">
          <div class="relative">
            <button
              type="button"
              data-testid="kanban-create-funnel"
              class="inline-flex h-9 max-w-[12rem] items-center gap-2 rounded-full border border-n-weak px-3 text-[13px] font-bold text-n-slate-12 hover:bg-n-alpha-2"
              :disabled="loadingFunnels"
              :aria-expanded="createFunnelMenuOpen"
              @click="toggleCreateFunnelMenu"
            >
              <span class="min-w-0 truncate">{{ createFunnelName }}</span>
              <span class="i-ri-arrow-down-s-line size-3.5 shrink-0 text-n-slate-11" />
            </button>
            <div
              v-if="createFunnelMenuOpen"
              class="absolute left-0 top-full z-30 mt-1 max-h-64 w-60 overflow-auto rounded-xl border border-n-weak bg-n-solid-1 py-1 shadow-lg"
            >
              <button
                v-for="funnel in availableFunnelsForSelector"
                :key="funnel.id"
                type="button"
                :data-testid="`kanban-create-funnel-${funnel.id}`"
                class="flex w-full items-center px-3 py-1.5 text-left text-xs hover:bg-n-alpha-2"
                :class="
                  String(funnel.id) === String(selectedFunnelId)
                    ? 'font-bold text-n-slate-12'
                    : 'text-n-slate-11'
                "
                @click="pickCreateFunnel(funnel)"
              >
                <span class="min-w-0 truncate">{{ funnel.name }}</span>
              </button>
            </div>
          </div>
          <span class="text-n-slate-11">·</span>
          <div class="relative">
            <button
              type="button"
              data-testid="kanban-create-stage"
              class="inline-flex h-9 max-w-[12rem] items-center gap-2 rounded-full border border-n-blue-11 px-3 text-[13px] font-bold text-n-blue-11 hover:bg-n-blue-11/10"
              :disabled="!availableStages.length"
              :aria-expanded="createStageMenuOpen"
              @click="toggleCreateStageMenu"
            >
              <span
                v-if="createStage?.color"
                class="size-2 shrink-0 rounded-full"
                :style="{ backgroundColor: createStage.color }"
              />
              <span class="min-w-0 truncate">
                {{ createStage?.name || t('KANBAN.FORM.STAGE.PLACEHOLDER') }}
              </span>
              <span class="i-ri-arrow-down-s-line size-3.5 shrink-0" />
            </button>
            <div
              v-if="createStageMenuOpen"
              class="absolute left-0 top-full z-30 mt-1 max-h-64 w-60 overflow-auto rounded-xl border border-n-weak bg-n-solid-1 py-1 shadow-lg"
            >
              <button
                v-for="stage in availableStages"
                :key="stage.id"
                type="button"
                :data-testid="`kanban-create-stage-${stage.id}`"
                class="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-n-alpha-2"
                @click="pickCreateStage(stage)"
              >
                <span class="size-2 shrink-0 rounded-full" :style="{ backgroundColor: stage.color }" />
                <span
                  class="min-w-0 flex-1 truncate"
                  :class="
                    String(stage.id) === String(form.funnel_stage)
                      ? 'font-bold text-n-slate-12'
                      : 'text-n-slate-11'
                  "
                >{{ stage.name }}</span>
                <span
                  v-if="String(stage.id) === String(form.funnel_stage)"
                  class="i-ri-check-line size-3.5 shrink-0 text-n-blue-11"
                />
              </button>
            </div>
          </div>
          <span v-if="createStageIdx >= 0" class="text-xs text-n-slate-11 tabular-nums">
            {{
              t('KANBAN.FORM.STAGE_POSITION', {
                current: createStageIdx + 1,
                total: availableStages.length,
              })
            }}
          </span>
        </div>
        <p class="m-0 text-xs text-n-slate-11">
          {{ t('KANBAN.FORM.ONE_CARD_PER_FUNNEL') }}
        </p>
      </div>

      <!-- Título -->
      <div class="flex flex-col gap-2">
        <label
          for="kanban-create-title"
          class="text-xs font-bold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('KANBAN.FORM.TITLE.LABEL') }}
        </label>
        <input
          id="kanban-create-title"
          v-model="form.title"
          type="text"
          data-testid="kanban-create-title"
          class="h-11 w-full rounded-xl border border-n-weak bg-n-solid-2 px-3 text-sm focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20"
          :placeholder="t('KANBAN.FORM.TITLE.PLACEHOLDER')"
          required
        />
        <p class="m-0 text-xs text-n-slate-11">
          {{ t('KANBAN.FORM.TITLE_IS_NOT_CONTACT') }}
        </p>
      </div>

      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <!-- Valor -->
        <div class="flex flex-col gap-2">
          <label
            for="kanban-create-value"
            class="text-xs font-bold uppercase tracking-wide text-n-slate-11"
          >
            {{ t('KANBAN.FORM.VALUE.LABEL') }}
          </label>
          <input
            id="kanban-create-value"
            :value="businessValueMask"
            type="text"
            inputmode="numeric"
            data-testid="kanban-create-value"
            class="h-11 w-full rounded-xl border border-n-weak bg-n-solid-2 px-3 text-sm font-bold tabular-nums focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20"
            :placeholder="t('KANBAN.FORM.VALUE.PLACEHOLDER')"
            @input="onBusinessValueInput"
          />
        </div>

        <!-- Responsável -->
        <div class="flex flex-col gap-2">
          <label class="text-xs font-bold uppercase tracking-wide text-n-slate-11">
            {{ t('KANBAN.FORM.AGENT.LABEL') }}
          </label>
          <div class="flex flex-wrap items-center gap-2">
            <span
              v-for="agentId in form.assigned_agents"
              :key="agentId"
              class="inline-flex h-8 items-center gap-1 rounded-full border border-n-weak px-2.5 text-xs text-n-slate-12"
            >
              {{ getAgentById(agentId)?.name || agentId }}
              <button
                type="button"
                class="text-n-slate-11 hover:text-n-ruby-11"
                :aria-label="t('KANBAN.FORM.AGENT.REMOVE')"
                @click="removeAgent(agentId)"
              >
                <span class="i-ri-close-line size-3.5" />
              </button>
            </span>
            <div v-if="!form.assigned_agents.length" class="relative flex-1 min-w-[10rem]">
              <input
                v-model="agentSearch"
                type="text"
                data-testid="kanban-create-agent"
                class="h-11 w-full rounded-xl border border-n-weak bg-n-solid-2 px-3 text-sm focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20"
                :placeholder="t('KANBAN.FORM.AGENT.PLACEHOLDER')"
                @focus="agentDropdownOpen = true"
              />
              <div
                v-if="agentDropdownOpen && filteredAgents.length"
                class="absolute left-0 top-full z-30 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-n-weak bg-n-solid-1 py-1 shadow-lg"
              >
                <button
                  v-for="agent in filteredAgents"
                  :key="agent.id"
                  type="button"
                  class="flex w-full items-center px-3 py-1.5 text-left text-xs text-n-slate-11 hover:bg-n-alpha-2"
                  @click="addAgent(agent); agentDropdownOpen = false"
                >
                  <span class="min-w-0 truncate">{{ agent.name }}</span>
                </button>
              </div>
            </div>
          </div>
          <p v-if="!form.assigned_agents.length" class="m-0 text-xs text-n-slate-11">
            {{ t('KANBAN.FORM.AGENT.INHERITS_FROM_CONVERSATION') }}
          </p>
        </div>
      </div>

      <!-- Descrição -->
      <div class="flex flex-col gap-2">
        <label
          for="kanban-create-description"
          class="text-xs font-bold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('KANBAN.FORM.DESCRIPTION.LABEL') }}
          <span class="font-normal normal-case tracking-normal text-n-slate-11">
            ({{ t('KANBAN.FORM.OPTIONAL') }})
          </span>
        </label>
        <textarea
          id="kanban-create-description"
          v-model="form.description"
          rows="3"
          data-testid="kanban-create-description"
          class="w-full rounded-xl border border-n-weak bg-n-solid-2 p-3 text-sm leading-relaxed focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20"
          :placeholder="t('KANBAN.FORM.DESCRIPTION.PLACEHOLDER')"
        />
      </div>

      <!-- Campos que existem, mas não pedem atenção de quem só quer criar. -->
      <button
        v-if="!showMoreCreateFields"
        type="button"
        data-testid="kanban-create-more"
        class="self-start text-sm font-bold text-n-blue-11 hover:underline"
        @click="showMoreCreateFields = true"
      >
        ＋ {{ t('KANBAN.FORM.MORE_FIELDS') }}
      </button>

      <div v-else class="flex flex-col gap-4 rounded-2xl border border-n-weak bg-n-solid-2 p-4">
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <!-- Prazo: era exclusivo da conta Stacklab por sobra de
               implementação, não por decisão de produto (Lucas, 2026-08-11). -->
          <div class="flex flex-col gap-2">
            <label
              for="kanban-create-deadline"
              class="text-xs font-bold uppercase tracking-wide text-n-slate-11"
            >
              {{ t('KANBAN.FORM.SCHEDULING.DEADLINE') }}
            </label>
            <input
              id="kanban-create-deadline"
              v-model="form.item_details.deadline_at"
              type="date"
              data-testid="kanban-create-deadline"
              class="h-11 w-full rounded-xl border border-n-weak bg-n-solid-1 px-3 text-sm focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20"
            />
          </div>
          <div class="flex flex-col gap-2">
            <label
              for="kanban-create-forecast"
              class="text-xs font-bold uppercase tracking-wide text-n-slate-11"
            >
              {{ t('KANBAN.FORM.CLOSING_FORECAST.LABEL') }}
            </label>
            <input
              id="kanban-create-forecast"
              v-model.number="form.item_details.closing_forecast_days"
              type="number"
              min="0"
              class="h-11 w-full rounded-xl border border-n-weak bg-n-solid-1 px-3 text-sm focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20"
              :placeholder="t('KANBAN.FORM.CLOSING_FORECAST.PLACEHOLDER')"
            />
          </div>
        </div>

        <!-- Campos globais do funil: os simples entram aqui; os de lista
             pedem uma UI própria e ficam no card, dito por extenso. -->
        <div
          v-for="attr in globalCustomAttributes.filter(a => !a.is_list)"
          :key="attr.name"
          class="flex flex-col gap-2"
        >
          <label class="text-xs font-bold uppercase tracking-wide text-n-slate-11">
            {{ attr.name }}
          </label>
          <input
            v-model="globalCustomAttributesValues[attr.name]"
            :type="
              attr.type === 'date'
                ? 'date'
                : attr.type === 'number'
                  ? 'number'
                  : 'text'
            "
            class="h-11 w-full rounded-xl border border-n-weak bg-n-solid-1 px-3 text-sm focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20"
            :placeholder="`${t('KANBAN.FORM.CUSTOM_FIELDS.ENTER_VALUE_FOR')} ${attr.name}`"
          />
        </div>
        <p
          v-if="globalCustomAttributes.some(a => a.is_list)"
          class="m-0 text-xs text-n-slate-11"
        >
          {{ t('KANBAN.FORM.LIST_FIELDS_IN_CARD') }}
        </p>
      </div>
      <!-- Duplicar: o mockup deixa isto discreto, no rodapé — quem cria
           um card do zero não precisa tropeçar nele antes dos campos. -->
      <div
        data-testid="kanban-create-duplicate"
        class="rounded-2xl border border-dashed border-n-weak bg-n-solid-2 p-3"
      >
        <div v-if="!duplicateSourceItem" class="space-y-3">
          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-2 min-w-0">
              <span
                class="i-ri-file-copy-line size-4 text-woot-500 shrink-0"
              />
              <span class="text-sm font-medium text-n-slate-12">Duplicar de outro kanban</span>
              <span class="text-xs text-n-slate-11 truncate">
                reaproveita a conversa do lead em outro funil
              </span>
            </div>
            <button
              type="button"
              class="text-xs font-medium text-woot-600 dark:text-woot-400 hover:underline shrink-0"
              @click="duplicateOpen = !duplicateOpen"
            >
              {{ duplicateOpen ? 'Fechar' : 'Procurar item' }}
            </button>
          </div>
          <div v-if="duplicateOpen" class="space-y-2">
            <input
              v-model="duplicateQuery"
              type="search"
              placeholder="Buscar por título, cliente, descrição..."
              class="w-full px-3 py-2 text-sm bg-n-solid-2 border border-n-weak rounded-xl focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
              @input="searchDuplicateItems(duplicateQuery)"
            />
            <div v-if="duplicateLoading" class="text-xs text-n-slate-11 px-1">
              Buscando...
            </div>
            <div
              v-else-if="
                duplicateQuery.length >= 2 && duplicateResults.length === 0
              "
              class="text-xs text-n-slate-11 px-1"
            >
              Nenhum item encontrado.
            </div>
            <div
              v-else-if="duplicateResults.length > 0"
              class="max-h-56 overflow-y-auto rounded-xl border border-n-weak bg-n-solid-1 divide-y divide-n-weak"
            >
              <button
                v-for="r in duplicateResults"
                :key="r.id"
                type="button"
                class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-2"
                @click="applyDuplicate(r)"
              >
                <div class="flex items-center justify-between gap-2">
                  <span class="font-medium text-n-slate-12 truncate">
                    {{ r.title || r.item_details?.title || 'Sem título' }}
                  </span>
                  <span
                    class="text-[10px] uppercase tracking-wide text-n-slate-10 shrink-0"
                  >
                    #{{ r.id }} · {{ r.funnel?.name || 'funil' }}
                  </span>
                </div>
                <div
                  v-if="
                    r.item_details?.customer_name ||
                    r.conversation?.contact?.name
                  "
                  class="text-xs text-n-slate-11 truncate"
                >
                  {{
                    r.item_details?.customer_name ||
                    r.conversation?.contact?.name
                  }}
                </div>
              </button>
            </div>
          </div>
        </div>
        <div v-else class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-2 min-w-0">
            <span
              class="i-ri-checkbox-circle-line size-4 text-emerald-500 shrink-0"
            />
            <span class="text-sm text-n-slate-12 truncate">
              Duplicando de
              <strong>#{{ duplicateSourceItem.id }}</strong>
              · {{ duplicateSourceItem.funnel?.name || 'funil de origem' }}
            </span>
          </div>
          <button
            type="button"
            class="text-xs font-medium text-rose-600 dark:text-rose-400 hover:underline shrink-0"
            @click="clearDuplicate"
          >
            Limpar
          </button>
        </div>
      </div>
    </div>

    <!-- Botões -->
    <div class="flex justify-end space-x-2 pt-4 p-3">
      <Button
        type="button"
        variant="ghost"
        color="slate"
        size="sm"
        @click="emit('close')"
      >
        {{ t('KANBAN.CANCEL') }}
      </Button>
      <Button
        type="submit"
        variant="solid"
        color="blue"
        size="sm"
        data-testid="kanban-form-submit"
        :is-loading="loading"
      >
        {{ isEditing ? t('KANBAN.SAVE') : t('KANBAN.FORM.CREATE_CARD') }}
      </Button>
    </div>
  </form>
</template>

<style lang="scss" scoped>
// Efeito de foco nos inputs
input,
textarea {
  @apply transition-shadow duration-200;

  &:focus {
    @apply shadow-md;
  }
}

// Animação suave do checkbox
input[type='checkbox'] {
  @apply transition-transform duration-200;

  &:checked {
    @apply scale-105;
  }
}

.loading-spinner {
  @apply w-4 h-4 border-2 border-n-weak border-t-woot-500 rounded-full animate-spin;
}

input[type='date'],
input[type='datetime-local'] {
  &::-webkit-calendar-picker-indicator {
    @apply dark:invert cursor-pointer;
  }
}

input[type='checkbox'],
input[type='radio'] {
  @apply cursor-pointer;
}

select {
  @apply cursor-pointer;
}

label {
  @apply cursor-pointer;
}

// Estilização do scroll
::-webkit-scrollbar {
  @apply w-2;
}

::-webkit-scrollbar-track {
  @apply bg-transparent;
}

::-webkit-scrollbar-thumb {
  @apply bg-n-alpha-2 rounded-full;

  &:hover {
    @apply bg-n-slate-9 bg-n-slate-8;
  }
}

// Ajuste para o scroll
form {
  @apply min-w-0 w-full transition-all;
}

// Ajuste para os grids responsivos
.grid {
  @apply min-w-0 w-full;
}

/* Mobile: ajustes específicos para abas */
@media (max-width: 768px) {
}

/* Extra small screens */
@media (max-width: 480px) {
}

// Cards com cores diferentes
.info-card {
  @apply relative overflow-hidden flex flex-col gap-1 p-3 rounded-xl transition-all duration-200;

  &:nth-child(1) {
    @apply bg-n-iris-3  border-n-iris-4 dark:border-n-iris-8/50;
  }

  &:nth-child(2) {
    @apply bg-n-teal-3  border-n-teal-4 dark:border-n-teal-8/50;
  }

  &:nth-child(3) {
    @apply bg-n-amber-3  border-n-amber-4 dark:border-n-amber-8/50;
  }

  &:nth-child(4) {
    @apply bg-n-iris-3  border-n-iris-4 dark:border-n-iris-8/50;
  }
}
</style>
